/**
 * Export functions for the fabric.js canvas editor.
 * Supports PNG, JPG, PDF, and SVG formats with optional bleed and crop marks.
 * Multi-page raster exports are packaged as a ZIP file.
 *
 * KEY DESIGN PRINCIPLE:
 * The document dimensions (widthMm × heightMm) are the source of truth.
 * The page rect is always at (0, 0) with those dimensions in pixels.
 * Exports always capture exactly the document area — quality multiplier
 * scales the output resolution up/down but never crops content.
 */

import { useProjectStore } from '@/stores/useProjectStore'
import { getCanvasInstance } from './canvasInstance'

export type ExportFormat = 'png' | 'jpg' | 'pdf' | 'svg'
export type ExportQuality = 1 | 2 | 4

export interface ExportOptions {
    format: ExportFormat
    /** Pixel density multiplier: 1 = 96 dpi, 2 = 192 dpi, 4 = 384 dpi */
    quality: ExportQuality
    /** Which page indices to export (0-based). If empty, exports all. */
    selectedPages: number[]
    cropMarks: boolean
    bleed: boolean
    projectName: string
    widthMm: number
    heightMm: number
}

const MM_TO_PX = 3.7795275591
const BLEED_MM = 3
const CROP_MARK_LENGTH_MM = 5
const CROP_MARK_OFFSET_MM = 2

// ── Helpers ────────────────────────────────────────────────────────────────────

function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
}

function downloadDataUrl(dataUrl: string, filename: string): void {
    const anchor = document.createElement('a')
    anchor.href = dataUrl
    anchor.download = filename
    anchor.click()
}

function sanitizeFilename(name: string): string {
    return name.replace(/[^a-z0-9_\-\s]/gi, '').replace(/\s+/g, '_') || 'diseno'
}

/**
 * The document (page rect) is always placed at (0, 0) in canvas coordinates
 * with size = widthMm * MM_TO_PX  ×  heightMm * MM_TO_PX.
 *
 * To export it faithfully we:
 *   1. Save the current viewport transform and canvas element size
 *   2. Set viewport to identity [1,0,0,1,0,0] so canvas coords = object coords
 *   3. Resize the canvas element to exactly the document pixel size
 *      (so the backing buffer covers the full document — no clipping)
 *   4. Render and capture with toDataURL (multiplier scales output resolution)
 *   5. Restore everything
 *
 * This guarantees the export matches the editor 1:1 regardless of browser
 * window size, zoom level, or pan position.
 */
function exportCanvasAsRaster(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    canvas: any,
    docWidthPx: number,
    docHeightPx: number,
    format: 'png' | 'jpg',
    multiplier: number,
): string {
    // 1. Save state
    const origVpt = canvas.viewportTransform ? [...canvas.viewportTransform] : [1, 0, 0, 1, 0, 0]
    const origW = canvas.getWidth()
    const origH = canvas.getHeight()

    // 2. Reset viewport to identity — object coordinates are now pixel coordinates
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0])

    // 3. Resize canvas element to match document dimensions exactly
    canvas.setDimensions({ width: docWidthPx, height: docHeightPx })
    canvas.renderAll()

    // 4. Capture — multiplier only scales the output resolution, coords stay the same
    const dataUrl = canvas.toDataURL({
        format,
        quality: format === 'jpg' ? 0.92 : 1,
        multiplier,
    })

    // 5. Restore
    canvas.setDimensions({ width: origW, height: origH })
    canvas.setViewportTransform(origVpt)
    canvas.renderAll()

    return dataUrl
}

/**
 * Same approach for SVG — reset viewport, resize canvas, export, restore.
 */
function exportCanvasAsSvgString(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    canvas: any,
    docWidthPx: number,
    docHeightPx: number,
): string {
    const origVpt = canvas.viewportTransform ? [...canvas.viewportTransform] : [1, 0, 0, 1, 0, 0]
    const origW = canvas.getWidth()
    const origH = canvas.getHeight()

    canvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    canvas.setDimensions({ width: docWidthPx, height: docHeightPx })
    canvas.renderAll()

    const svgString = canvas.toSVG({
        width: docWidthPx + 'px',
        height: docHeightPx + 'px',
        viewBox: { x: 0, y: 0, width: docWidthPx, height: docHeightPx },
    })

    canvas.setDimensions({ width: origW, height: origH })
    canvas.setViewportTransform(origVpt)
    canvas.renderAll()

    return svgString
}

/**
 * Creates a temporary off-screen canvas from saved canvas_json.
 * Sized to exactly the document dimensions so nothing is clipped.
 */
async function createTempCanvas(
    canvasJson: Record<string, unknown>,
    docWidthPx: number,
    docHeightPx: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
    const { Canvas } = await import('fabric')

    const el = document.createElement('canvas')
    el.width = docWidthPx
    el.height = docHeightPx

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tempCanvas = new Canvas(el) as any
    await tempCanvas.loadFromJSON(canvasJson)
    // Ensure identity viewport — the page rect is at (0,0) in the saved JSON
    tempCanvas.setViewportTransform([1, 0, 0, 1, 0, 0])
    tempCanvas.setDimensions({ width: docWidthPx, height: docHeightPx })
    tempCanvas.renderAll()

    return tempCanvas
}

/**
 * Draws printer crop marks onto a jsPDF instance.
 */
function drawCropMarks(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    doc: any,
    x: number, y: number, w: number, h: number,
    offset: number, length: number,
): void {
    doc.setDrawColor(0)
    doc.setLineWidth(0.25)

    const r = x + w
    const b = y + h

    doc.line(x - offset - length, y, x - offset, y)
    doc.line(x, y - offset - length, x, y - offset)
    doc.line(r + offset, y, r + offset + length, y)
    doc.line(r, y - offset - length, r, y - offset)
    doc.line(x - offset - length, b, x - offset, b)
    doc.line(x, b + offset, x, b + offset + length)
    doc.line(r + offset, b, r + offset + length, b)
    doc.line(r, b + offset, r, b + offset + length)
}

function getPageIndices(selectedPages: number[], totalPages: number): number[] {
    if (selectedPages.length === 0) {
        return Array.from({ length: totalPages }, (_, i) => i)
    }
    return selectedPages.filter((i) => i < totalPages)
}

// ── Per-page export helpers ────────────────────────────────────────────────────

async function exportPageAsRaster(
    pageIndex: number,
    docWidthPx: number,
    docHeightPx: number,
    format: 'png' | 'jpg',
    quality: ExportQuality,
): Promise<string> {
    const { pages } = useProjectStore.getState()
    const { useEditorStore } = await import('@/stores/useEditorStore')
    const currentPageIndex = useEditorStore.getState().currentPageIndex
    const liveCanvas = getCanvasInstance()

    // Use live canvas for the currently active page — it has the latest state
    if (liveCanvas && pageIndex === currentPageIndex) {
        return exportCanvasAsRaster(liveCanvas, docWidthPx, docHeightPx, format, quality)
    }

    // Other pages: load from saved JSON into a temp canvas
    const page = pages[pageIndex]
    const tempCanvas = await createTempCanvas(page.canvas_json, docWidthPx, docHeightPx)
    const dataUrl = tempCanvas.toDataURL({
        format,
        quality: format === 'jpg' ? 0.92 : 1,
        multiplier: quality,
    })
    tempCanvas.dispose()
    return dataUrl
}

async function exportPageAsSvg(
    pageIndex: number,
    docWidthPx: number,
    docHeightPx: number,
): Promise<string> {
    const { pages } = useProjectStore.getState()
    const { useEditorStore } = await import('@/stores/useEditorStore')
    const currentPageIndex = useEditorStore.getState().currentPageIndex
    const liveCanvas = getCanvasInstance()

    if (liveCanvas && pageIndex === currentPageIndex) {
        return exportCanvasAsSvgString(liveCanvas, docWidthPx, docHeightPx)
    }

    const page = pages[pageIndex]
    const tempCanvas = await createTempCanvas(page.canvas_json, docWidthPx, docHeightPx)
    const svgString = tempCanvas.toSVG({
        width: docWidthPx + 'px',
        height: docHeightPx + 'px',
        viewBox: { x: 0, y: 0, width: docWidthPx, height: docHeightPx },
    })
    tempCanvas.dispose()
    return svgString
}

// ── Format-specific exports ────────────────────────────────────────────────────

async function exportRaster(options: ExportOptions): Promise<void> {
    const { pages } = useProjectStore.getState()
    const rasterFormat = options.format as 'png' | 'jpg'
    const safeProject = sanitizeFilename(options.projectName)
    const pageIndices = getPageIndices(options.selectedPages, pages.length)
    const docWidthPx = Math.round(options.widthMm * MM_TO_PX)
    const docHeightPx = Math.round(options.heightMm * MM_TO_PX)

    if (pageIndices.length === 1) {
        const dataUrl = await exportPageAsRaster(pageIndices[0], docWidthPx, docHeightPx, rasterFormat, options.quality)
        const ext = rasterFormat === 'jpg' ? 'jpg' : 'png'
        downloadDataUrl(dataUrl, `${safeProject}.${ext}`)
        return
    }

    const JSZip = (await import('jszip')).default
    const zip = new JSZip()

    for (const idx of pageIndices) {
        const dataUrl = await exportPageAsRaster(idx, docWidthPx, docHeightPx, rasterFormat, options.quality)
        const ext = rasterFormat === 'jpg' ? 'jpg' : 'png'
        const base64 = dataUrl.split(',')[1]
        zip.file(`${safeProject}_pagina_${idx + 1}.${ext}`, base64, { base64: true })
    }

    const blob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(blob, `${safeProject}.zip`)
}

async function exportSvg(options: ExportOptions): Promise<void> {
    const { pages } = useProjectStore.getState()
    const safeProject = sanitizeFilename(options.projectName)
    const pageIndices = getPageIndices(options.selectedPages, pages.length)
    const docWidthPx = Math.round(options.widthMm * MM_TO_PX)
    const docHeightPx = Math.round(options.heightMm * MM_TO_PX)

    if (pageIndices.length === 1) {
        const svgString = await exportPageAsSvg(pageIndices[0], docWidthPx, docHeightPx)
        const blob = new Blob([svgString], { type: 'image/svg+xml' })
        downloadBlob(blob, `${safeProject}.svg`)
        return
    }

    const JSZip = (await import('jszip')).default
    const zip = new JSZip()

    for (const idx of pageIndices) {
        const svgString = await exportPageAsSvg(idx, docWidthPx, docHeightPx)
        zip.file(`${safeProject}_pagina_${idx + 1}.svg`, svgString)
    }

    const blob = await zip.generateAsync({ type: 'blob' })
    downloadBlob(blob, `${safeProject}.zip`)
}

async function exportPdf(options: ExportOptions): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let jsPDFCtor: any
    try {
        const jspdf = await import('jspdf')
        jsPDFCtor = jspdf.jsPDF
    } catch {
        throw new Error('PDF export requires jspdf package')
    }

    const { pages } = useProjectStore.getState()
    const { widthMm, heightMm, quality, cropMarks, bleed, projectName } = options
    const safeProject = sanitizeFilename(projectName)
    const pageIndices = getPageIndices(options.selectedPages, pages.length)
    const docWidthPx = Math.round(widthMm * MM_TO_PX)
    const docHeightPx = Math.round(heightMm * MM_TO_PX)

    const bleedMm = bleed ? BLEED_MM : 0
    const pageWidthMm = widthMm + bleedMm * 2
    const pageHeightMm = heightMm + bleedMm * 2

    const pdfWidthMm = cropMarks
        ? pageWidthMm + (CROP_MARK_OFFSET_MM + CROP_MARK_LENGTH_MM) * 2
        : pageWidthMm
    const pdfHeightMm = cropMarks
        ? pageHeightMm + (CROP_MARK_OFFSET_MM + CROP_MARK_LENGTH_MM) * 2
        : pageHeightMm

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const doc = new jsPDFCtor({
        orientation: widthMm >= heightMm ? 'landscape' : 'portrait',
        unit: 'mm',
        format: [pdfWidthMm, pdfHeightMm],
    }) as any

    const contentX = cropMarks ? CROP_MARK_OFFSET_MM + CROP_MARK_LENGTH_MM : 0
    const contentY = cropMarks ? CROP_MARK_OFFSET_MM + CROP_MARK_LENGTH_MM : 0

    for (let i = 0; i < pageIndices.length; i++) {
        if (i > 0) doc.addPage([pdfWidthMm, pdfHeightMm])

        const dataUrl = await exportPageAsRaster(
            pageIndices[i], docWidthPx, docHeightPx, 'png', quality,
        )

        doc.addImage(
            dataUrl, 'PNG',
            contentX + bleedMm, contentY + bleedMm,
            widthMm, heightMm,
        )

        if (cropMarks) {
            drawCropMarks(
                doc,
                contentX + bleedMm, contentY + bleedMm,
                widthMm, heightMm,
                CROP_MARK_OFFSET_MM, CROP_MARK_LENGTH_MM,
            )
        }
    }

    doc.save(`${safeProject}.pdf`)
}

// ── Public API ─────────────────────────────────────────────────────────────────

export async function exportDesign(options: ExportOptions): Promise<void> {
    if (options.format === 'pdf') {
        await exportPdf(options)
    } else if (options.format === 'svg') {
        await exportSvg(options)
    } else {
        await exportRaster(options)
    }
}
